import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ShoppingCart, Gift } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetTitle,
} from './ui/sheet';

export const Navbar = () => {
    const location = useLocation();
    const { itemCount } = useCart();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'Mantrailing', path: '/mantrailing' },
        { name: 'Gallery', path: '/gallery' },
        { name: 'Gift Vouchers', path: '/gift-vouchers' },
        { name: 'FAQ', path: '/faq' },
        { name: 'Contact', path: '/contact' },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border/50 shadow-sm" data-testid="navbar">
            <div className="container-custom">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
                        <img 
                            src="https://customer-assets.emergentagent.com/job_canine-training-pro/artifacts/k6jr7ouk_Laura%20logo%20CC.jpeg" 
                            alt="Craven Canines" 
                            className="h-12 w-auto group-hover:scale-105 transition-transform duration-300"
                        />
                        <span className="text-xl font-bold text-foreground font-serif">
                            Craven <span className="text-primary">Canines</span>
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`nav-link text-base ${isActive(link.path) ? 'active' : ''}`}
                                data-testid={`nav-link-${link.name.toLowerCase()}`}
                            >
                                {link.name}
                            </Link>
                        ))}
                    </div>

                    {/* Cart & Mobile Menu */}
                    <div className="flex items-center gap-4">
                        {/* Cart Button */}
                        <Link to="/cart" data-testid="nav-cart-button">
                            <Button
                                variant="outline"
                                size="icon"
                                className="relative rounded-full border-primary/20 hover:bg-primary/5 hover:border-primary/40"
                            >
                                <ShoppingCart className="w-5 h-5 text-primary" />
                                {itemCount > 0 && (
                                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-accent-foreground text-xs font-bold rounded-full flex items-center justify-center cart-badge" data-testid="cart-count">
                                        {itemCount}
                                    </span>
                                )}
                            </Button>
                        </Link>

                        {/* Mobile Menu Button */}
                        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                            <SheetTrigger asChild className="md:hidden">
                                <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                                    <Menu className="w-6 h-6" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-[280px] bg-white">
                                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                                <div className="flex flex-col gap-6 mt-8">
                                    {navLinks.map((link) => (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className={`text-lg font-medium px-4 py-2 rounded-lg transition-colors ${
                                                isActive(link.path)
                                                    ? 'bg-primary/10 text-primary'
                                                    : 'text-foreground hover:bg-secondary'
                                            }`}
                                            data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                                        >
                                            {link.name}
                                        </Link>
                                    ))}
                                    <Link
                                        to="/cart"
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="flex items-center gap-3 text-lg font-medium px-4 py-2 rounded-lg text-foreground hover:bg-secondary"
                                        data-testid="mobile-nav-cart"
                                    >
                                        <ShoppingCart className="w-5 h-5" />
                                        Cart ({itemCount})
                                    </Link>
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </div>
        </nav>
    );
};
